import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { markUserActive } from "@/api/client";
import { claimActivityMark } from "@/lib/appBoot";

/**
 * Keeps the daily-active signal correct for a tab left open past midnight.
 *
 * The page-load signal (see lib/appBoot.ts) covers the normal case. It cannot
 * cover a session that stays on one page load across a date boundary, so this
 * marks the user active on their first in-app navigation of a new UTC day.
 *
 * A navigation means the user clicked something. A window refocus or a
 * background refetch produces no location change, so a tab that merely sits
 * open is still not counted, which is the intended definition.
 *
 * Costs one extra request per tab per day at most, and only after a date
 * rollover: same-day navigations short-circuit before any request.
 */
export function useActivityMark(): void {
  const location = useLocation();
  const skipInitialRender = useRef(true);

  useEffect(() => {
    // The initial render is already covered by the page-load signal, which
    // ThemeSync's settings fetch carries.
    if (skipInitialRender.current) {
      skipInitialRender.current = false;
      return;
    }

    if (!claimActivityMark()) return;

    // Fire and forget: this is telemetry, and a failure must never surface to
    // the user or break navigation. The response is already in the query cache.
    void markUserActive().catch(() => {});
  }, [location.pathname]);
}
